/* eslint-disable @typescript-eslint/no-explicit-any */
import { ApiLogInfo } from "@/api-services/types/api.types";
import * as Sentry from "@sentry/browser";

/**
 * Interface representing a single log entry in the logger.
 */
interface LogEntry {
  level: Sentry.SeverityLevel;
  message: string;
  component?: string;
  user?: string;
  timeStamp: Date;
  metadata?: any;
}

/**
 * Logger class that manages logging for various levels (info, warn, error, debug)
 * and handles API-specific logs, error reporting (Sentry), and in-memory storage.
 */
class Logger {
  private logs: LogEntry[] = [];
  private maxLogs = 1000;

  /**
   * Creates a log entry object with enriched metadata.
   *
   * @param level - Severity level (info, warning, error, debug)
   * @param message - Log message
   * @param metadata - Additional metadata for context
   * @returns A log entry object
   */
  private createLogEntry(
    level: LogEntry["level"],
    message: string,
    metadata?: any
  ): LogEntry {
    return {
      level,
      message,
      timeStamp: new Date(),
      metadata: {
        ...metadata,
        userAgent: navigator.userAgent,
        url: window.location.href,
      },
    };
  }

  /**
   * Logs an info-level message to the console and in memory.
   */
  info(message: string, metadata?: any) {
    const entry = this.createLogEntry("info", message, metadata);
    this.logs.push(entry);
    console.log(`[INFO] ${message}`, metadata);
    this.trimLogs();
  }

  /**
   * Logs a warning-level message to the console and in memory.
   */
  warn(message: string, metadata?: any) {
    const entry = this.createLogEntry("warning", message, metadata);
    this.logs.push(entry);
    console.warn(`[WARN] ${message}`, metadata);
    this.trimLogs();
  }

  /**
   * Logs an error-level message to the console, in memory, and sends it to error reporting (Sentry).
   */
  error(message: string, metadata?: any) {
    const entry = this.createLogEntry("error", message, metadata);
    this.logs.push(entry);
    console.error(`[ERROR] ${message}`, metadata);
    this.trimLogs();

    // Send to crashlytics/error reporting
    this.reportError(entry);
  }

  /**
   * Logs a debug-level message to the console in development mode only.
   */
  debug(message: string, metadata?: any) {
    const entry = this.createLogEntry("debug", message, metadata);
    this.logs.push(entry);
    if (process.env.NODE_ENV === "development") {
      console.debug(`[DEBUG] ${message}`, metadata);
    }
    this.trimLogs();
  }

  /**
   * Specialized logger for API error logs.
   */
  apiError(message: ApiLogInfo) {
    const entry = this.createLogEntry(
      "error",
      JSON.stringify(message),
      message
    );
    this.logs.push(entry);
    if (process.env.NODE_ENV === "development") {
      console.error(
        `[API ERROR]`,
        `[${entry.timeStamp.toISOString()}] [${entry.level}] [${
          message.url
        }] [${message.requestId}] [${message.responseTime}ms] ${
          typeof message.httpCode !== "undefined" ? `${message.httpCode} ` : ""
        }${
          typeof message.errorCode !== "undefined"
            ? `${message.errorCode}: `
            : ""
        }${typeof message !== "undefined" ? message.message : ""}`
      );
    }
    this.trimLogs();
    this.reportError(entry);
  }

  /**
   * Specialized logger for API success/info logs.
   */
  apiInfo(message: ApiLogInfo) {
    const entry = this.createLogEntry("info", JSON.stringify(message), message);
    if (process.env.NODE_ENV === "development") {
      console.log(
        `[API INFO]`,
        `[${entry.timeStamp.toISOString()}] [${entry.level}] [${
          message.url
        }] [${message.requestId}] [${message.responseTime}ms]`
      );
    }
    this.logs.push(entry);
    this.trimLogs();
  }

  /**
   * Trims the in-memory log storage to prevent it from growing beyond maxLogs.
   */
  private trimLogs() {
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }
  }

  /**
   * Sends an error log entry to an external error tracking system (e.g., Sentry).
   */
  private reportError(entry: LogEntry) {
    // Integration with error reporting service (Sentry, etc.)
    try {
      // Mock error reporting
      if (typeof window !== "undefined" && Sentry?.captureException) {
        const { metadata } = entry;
        const errorMessage = `${metadata?.errorCode || "Unknown Error"} - ${
          metadata?.requestId || ""
        }${
          metadata?.customerRefId !== undefined && metadata.customerRefId !== ""
            ? ` - ${metadata.customerRefId}`
            : ""
        }`;
        Sentry.captureException(new Error(errorMessage), {
          level: entry.level,
          tags: {
            screenId: entry.metadata?.screenId,
            requestId: entry.metadata?.requestId,
            apiPath: entry.metadata?.url,
            errorCode: entry.metadata?.errorCode,
          },
          extra: {
            ...entry.metadata,
            level: entry.level,
            timeStamp: entry.timeStamp.toISOString(),
          },
        });
      }
    } catch (error) {
      console.error("Failed to report error:", error);
    }
  }

  /**
   * Returns the list of logs, optionally filtered by severity level.
   */
  getLogs(level?: LogEntry["level"]): LogEntry[] {
    if (level) {
      return this.logs.filter((log) => log.level === level);
    }
    return [...this.logs];
  }

  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  clearLogs() {
    this.logs = [];
  }
}

export const onRenderCallback = (id, phase, actualDuration) => {
  logger.info(
    `[Profiler][${id}] ${phase} render took ${actualDuration.toFixed(2)}ms`
  );
};

export const logger = new Logger();
